import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Palette,
  Building2,
  Upload,
  Save,
  Eye,
  Undo2,
  Download,
  Check,
  Loader2,
  Image,
  Type,
  Layout,
  Moon,
  Sparkles,
  RefreshCw,
} from 'lucide-react';
import { api } from '../../lib/api';
import { cn } from '../../lib/utils';

interface ColorScale {
  '50': string;
  '100': string;
  '200': string;
  '300': string;
  '400': string;
  '500': string;
  '600': string;
  '700': string;
  '800': string;
  '900': string;
  '950': string;
}

interface TenantTheme {
  id: string;
  tenantId: string;
  brandName: string;
  displayName?: string;
  logoUrl?: string;
  faviconUrl?: string;
  primaryColors: ColorScale;
  accentColors: ColorScale;
  surfaceColors: ColorScale;
  fontFamily?: string;
  fontFamilyMono?: string;
  borderRadius?: string;
  buttonStyle: 'SOLID' | 'OUTLINE' | 'GHOST';
  sidebarStyle: 'DARK' | 'LIGHT' | 'TRANSPARENT';
  loginBgImage?: string;
  loginBgGradient?: string;
  darkModeEnabled: boolean;
  defaultMode: 'LIGHT' | 'DARK' | 'SYSTEM';
  status: 'DRAFT' | 'PUBLISHED';
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface ThemeTemplate {
  id: string;
  name: string;
  description?: string;
  thumbnailUrl?: string;
  primaryColors: ColorScale;
  accentColors: ColorScale;
  surfaceColors: ColorScale;
  fontFamily?: string;
  borderRadius?: string;
  buttonStyle: string;
  sidebarStyle: string;
  category?: string;
  isDefault: boolean;
}

interface Tenant {
  id: string;
  name: string;
  subdomain?: string;
  status: string;
}

// Default color scales
const defaultPrimaryColors: ColorScale = {
  '50': '236 254 255',
  '100': '207 250 254',
  '200': '165 243 252',
  '300': '103 232 249',
  '400': '34 211 238',
  '500': '6 182 212',
  '600': '8 145 178',
  '700': '14 116 144',
  '800': '21 94 117',
  '900': '22 78 99',
  '950': '8 51 68',
};

const defaultAccentColors: ColorScale = {
  '50': '254 242 242',
  '100': '254 226 226',
  '200': '254 202 202',
  '300': '252 165 165',
  '400': '248 113 113',
  '500': '239 68 68',
  '600': '220 38 38',
  '700': '185 28 28',
  '800': '153 27 27',
  '900': '127 29 29',
  '950': '69 10 10',
};

const defaultSurfaceColors: ColorScale = {
  '50': '250 250 250',
  '100': '244 244 245',
  '200': '228 228 231',
  '300': '212 212 216',
  '400': '161 161 170',
  '500': '113 113 122',
  '600': '82 82 91',
  '700': '63 63 70',
  '800': '39 39 42',
  '900': '24 24 27',
  '950': '9 9 11',
};

// Color picker component
function ColorScaleEditor({
  label,
  colors,
  onChange,
}: {
  label: string;
  colors: ColorScale;
  onChange: (colors: ColorScale) => void;
}) {
  const shades = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'] as const;

  const rgbToHex = (rgb: string): string => {
    const parts = rgb.split(' ').map(Number);
    if (parts.length !== 3) return '#000000';
    return '#' + parts.map(x => x.toString(16).padStart(2, '0')).join('');
  };

  const hexToRgb = (hex: string): string => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return '0 0 0';
    return `${parseInt(result[1], 16)} ${parseInt(result[2], 16)} ${parseInt(result[3], 16)}`;
  };

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-surface-300">{label}</h4>
      <div className="flex gap-1">
        {shades.map((shade) => (
          <div key={shade} className="flex-1">
            <input
              type="color"
              value={rgbToHex(colors[shade])}
              onChange={(e) => onChange({ ...colors, [shade]: hexToRgb(e.target.value) })}
              className="w-full h-8 rounded cursor-pointer border-0"
              title={`Shade ${shade}`}
            />
            <p className="text-[10px] text-center text-surface-500 mt-1">{shade}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// Preview component
function ThemePreview({ theme }: { theme: Partial<TenantTheme> }) {
  const primaryColor = theme.primaryColors?.['500'] || '6 182 212';
  const accentColor = theme.accentColors?.['500'] || '239 68 68';
  const surfaceColor = theme.surfaceColors?.['900'] || '24 24 27';

  return (
    <div
      className="rounded-xl overflow-hidden border border-surface-700"
      style={{ backgroundColor: `rgb(${surfaceColor})` }}
    >
      {/* Header */}
      <div className="h-12 flex items-center px-4 border-b border-surface-700">
        {theme.logoUrl ? (
          <img src={theme.logoUrl} alt="Logo" className="h-8 w-auto" />
        ) : (
          <div
            className="h-8 w-8 rounded-lg flex items-center justify-center text-white text-sm font-bold"
            style={{ backgroundColor: `rgb(${primaryColor})` }}
          >
            {theme.brandName?.charAt(0) || 'B'}
          </div>
        )}
        <span className="ml-3 text-white font-medium text-sm">
          {theme.brandName || 'Brand Name'}
        </span>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        <div className="flex gap-2">
          <button
            className="px-3 py-1.5 rounded-lg text-white text-xs font-medium"
            style={{ backgroundColor: `rgb(${primaryColor})` }}
          >
            Primary Button
          </button>
          <button
            className="px-3 py-1.5 rounded-lg text-white text-xs font-medium"
            style={{ backgroundColor: `rgb(${accentColor})` }}
          >
            Accent Button
          </button>
        </div>
        <div className="h-16 rounded-lg bg-surface-800/50 flex items-center justify-center">
          <span className="text-surface-400 text-xs">Content Area</span>
        </div>
      </div>
    </div>
  );
}

export function ThemeEditor() {
  const queryClient = useQueryClient();
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'colors' | 'branding' | 'layout'>('colors');
  const [showTemplates, setShowTemplates] = useState(false);
  const [formData, setFormData] = useState<Partial<TenantTheme>>({
    brandName: '',
    displayName: '',
    primaryColors: defaultPrimaryColors,
    accentColors: defaultAccentColors,
    surfaceColors: defaultSurfaceColors,
    fontFamily: 'Inter',
    borderRadius: '12px',
    buttonStyle: 'SOLID',
    sidebarStyle: 'DARK',
    darkModeEnabled: true,
    defaultMode: 'DARK',
  });

  // Fetch tenants
  const { data: tenants, isLoading: loadingTenants } = useQuery({
    queryKey: ['super-admin-tenants'],
    queryFn: async () => {
      const res = await api.get('/super-admin/tenants');
      return res.data as Tenant[];
    },
  });

  // Fetch theme for selected tenant
  const { data: theme, isLoading: loadingTheme, error: themeError } = useQuery({
    queryKey: ['tenant-theme', selectedTenantId],
    queryFn: async () => {
      const res = await api.get(`/themes/${selectedTenantId}`);
      return res.data as TenantTheme;
    },
    enabled: !!selectedTenantId,
    retry: false,
  });

  // Fetch templates
  const { data: templates } = useQuery({
    queryKey: ['theme-templates'],
    queryFn: async () => {
      const res = await api.get('/theme-templates');
      return res.data as ThemeTemplate[];
    },
  });

  // Update form when theme loads
  useEffect(() => {
    if (theme) {
      setFormData(theme);
    } else if (selectedTenantId && themeError) {
      // Reset to defaults if no theme exists
      setFormData({
        brandName: tenants?.find(t => t.id === selectedTenantId)?.name || '',
        displayName: '',
        primaryColors: defaultPrimaryColors,
        accentColors: defaultAccentColors,
        surfaceColors: defaultSurfaceColors,
        fontFamily: 'Inter',
        borderRadius: '12px',
        buttonStyle: 'SOLID',
        sidebarStyle: 'DARK',
        darkModeEnabled: true,
        defaultMode: 'DARK',
      });
    }
  }, [theme, selectedTenantId, themeError, tenants]);

  // Create/Update theme mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (theme) {
        return api.put(`/themes/${selectedTenantId}`, formData);
      } else {
        return api.post(`/themes/${selectedTenantId}`, formData);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-theme', selectedTenantId] });
    },
  });

  // Publish theme mutation
  const publishMutation = useMutation({
    mutationFn: async () => api.post(`/themes/${selectedTenantId}/publish`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-theme', selectedTenantId] });
    },
  });

  // Revert theme mutation
  const revertMutation = useMutation({
    mutationFn: async () => api.post(`/themes/${selectedTenantId}/revert`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-theme', selectedTenantId] });
    },
  });

  // Apply template mutation
  const applyTemplateMutation = useMutation({
    mutationFn: async (templateId: string) => 
      api.post(`/theme-templates/${selectedTenantId}/apply/${templateId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-theme', selectedTenantId] });
      setShowTemplates(false);
    },
  });

  // Export theme
  const handleExport = async () => {
    if (!selectedTenantId) return;
    const res = await api.get(`/themes/${selectedTenantId}/export`);
    const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `theme-${selectedTenantId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Upload logo
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedTenantId) return;
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    const res = await api.post(`/themes/${selectedTenantId}/logo`, formDataUpload, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    setFormData(prev => ({ ...prev, logoUrl: res.data.url }));
  };

  // Upload favicon
  const handleFaviconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedTenantId) return;
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    const res = await api.post(`/themes/${selectedTenantId}/favicon`, formDataUpload, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    setFormData(prev => ({ ...prev, faviconUrl: res.data.url }));
  };

  const tabs = [
    { id: 'colors', label: 'Cores', icon: Palette },
    { id: 'branding', label: 'Marca', icon: Image },
    { id: 'layout', label: 'Layout', icon: Layout },
  ] as const;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Editor de Temas</h1>
          <p className="text-surface-400 mt-1">
            Personalize a identidade visual de cada tenant
          </p>
        </div>
        <div className="flex items-center gap-3">
          {selectedTenantId && theme && (
            <>
              <button
                onClick={() => setShowTemplates(true)}
                className="btn-secondary flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                Templates
              </button>
              <button
                onClick={handleExport}
                className="btn-secondary flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Exportar
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tenant Selector */}
      <div className="glass rounded-xl p-6">
        <div className="flex items-center gap-4">
          <Building2 className="w-5 h-5 text-primary-400" />
          <div className="flex-1">
            <label className="block text-sm font-medium text-surface-300 mb-2">
              Selecione o Tenant
            </label>
            <select
              value={selectedTenantId || ''}
              onChange={(e) => setSelectedTenantId(e.target.value || null)}
              className="input w-full max-w-md"
              disabled={loadingTenants}
            >
              <option value="">Selecione um tenant...</option>
              {tenants?.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name} {tenant.subdomain && `(${tenant.subdomain})`}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Editor */}
      {selectedTenantId && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Editor Panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* Status Bar */}
            {theme && (
              <div className="glass rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={cn(
                    'px-2 py-1 rounded text-xs font-medium',
                    theme.status === 'PUBLISHED' 
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-yellow-500/20 text-yellow-400'
                  )}>
                    {theme.status === 'PUBLISHED' ? 'Publicado' : 'Rascunho'}
                  </span>
                  {theme.publishedAt && (
                    <span className="text-xs text-surface-500">
                      Publicado em {new Date(theme.publishedAt).toLocaleDateString('pt-BR')}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => revertMutation.mutate()}
                    disabled={revertMutation.isPending}
                    className="btn-secondary text-sm flex items-center gap-2"
                  >
                    <Undo2 className="w-4 h-4" />
                    Reverter
                  </button>
                  <button
                    onClick={() => publishMutation.mutate()}
                    disabled={publishMutation.isPending || theme.status === 'PUBLISHED'}
                    className="btn-primary text-sm flex items-center gap-2"
                  >
                    {publishMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    Publicar
                  </button>
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="glass rounded-xl overflow-hidden">
              <div className="flex border-b border-surface-700">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      'flex-1 px-4 py-3 flex items-center justify-center gap-2 text-sm font-medium transition-colors',
                      activeTab === tab.id
                        ? 'bg-primary-500/10 text-primary-400 border-b-2 border-primary-500'
                        : 'text-surface-400 hover:text-surface-200'
                    )}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="p-6">
                {loadingTheme ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
                  </div>
                ) : (
                  <>
                    {/* Colors Tab */}
                    {activeTab === 'colors' && (
                      <div className="space-y-6">
                        <ColorScaleEditor
                          label="Cores Primárias"
                          colors={formData.primaryColors || defaultPrimaryColors}
                          onChange={(colors) => setFormData(prev => ({ ...prev, primaryColors: colors }))}
                        />
                        <ColorScaleEditor
                          label="Cores de Destaque (Accent)"
                          colors={formData.accentColors || defaultAccentColors}
                          onChange={(colors) => setFormData(prev => ({ ...prev, accentColors: colors }))}
                        />
                        <ColorScaleEditor
                          label="Cores de Superfície"
                          colors={formData.surfaceColors || defaultSurfaceColors}
                          onChange={(colors) => setFormData(prev => ({ ...prev, surfaceColors: colors }))}
                        />
                      </div>
                    )}

                    {/* Branding Tab */}
                    {activeTab === 'branding' && (
                      <div className="space-y-6">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-surface-300 mb-2">
                              Nome da Marca
                            </label>
                            <input
                              type="text"
                              value={formData.brandName || ''}
                              onChange={(e) => setFormData(prev => ({ ...prev, brandName: e.target.value }))}
                              className="input w-full"
                              placeholder="Ex: Banco XYZ"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-surface-300 mb-2">
                              Nome de Exibição
                            </label>
                            <input
                              type="text"
                              value={formData.displayName || ''}
                              onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                              className="input w-full"
                              placeholder="Ex: Banco XYZ - Seu banco digital"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-surface-300 mb-2">
                              Logo
                            </label>
                            <div className="flex items-center gap-4">
                              {formData.logoUrl && (
                                <img src={formData.logoUrl} alt="Logo" className="h-12 w-auto rounded" />
                              )}
                              <label className="btn-secondary cursor-pointer flex items-center gap-2">
                                <Upload className="w-4 h-4" />
                                Upload
                                <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                              </label>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-surface-300 mb-2">
                              Favicon
                            </label>
                            <div className="flex items-center gap-4">
                              {formData.faviconUrl && (
                                <img src={formData.faviconUrl} alt="Favicon" className="h-8 w-8 rounded" />
                              )}
                              <label className="btn-secondary cursor-pointer flex items-center gap-2">
                                <Upload className="w-4 h-4" />
                                Upload
                                <input type="file" accept="image/*" onChange={handleFaviconUpload} className="hidden" />
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Layout Tab */}
                    {activeTab === 'layout' && (
                      <div className="space-y-6">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-surface-300 mb-2">
                              <Type className="w-4 h-4 inline mr-2" />
                              Fonte Principal
                            </label>
                            <select
                              value={formData.fontFamily || 'Inter'}
                              onChange={(e) => setFormData(prev => ({ ...prev, fontFamily: e.target.value }))}
                              className="input w-full"
                            >
                              <option value="Inter">Inter</option>
                              <option value="Roboto">Roboto</option>
                              <option value="Open Sans">Open Sans</option>
                              <option value="Poppins">Poppins</option>
                              <option value="Montserrat">Montserrat</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-surface-300 mb-2">
                              Border Radius
                            </label>
                            <select
                              value={formData.borderRadius || '12px'}
                              onChange={(e) => setFormData(prev => ({ ...prev, borderRadius: e.target.value }))}
                              className="input w-full"
                            >
                              <option value="0px">Sem arredondamento</option>
                              <option value="4px">Pequeno (4px)</option>
                              <option value="8px">Médio (8px)</option>
                              <option value="12px">Grande (12px)</option>
                              <option value="16px">Extra Grande (16px)</option>
                            </select>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-surface-300 mb-2">
                              Estilo dos Botões
                            </label>
                            <select
                              value={formData.buttonStyle || 'SOLID'}
                              onChange={(e) => setFormData(prev => ({ ...prev, buttonStyle: e.target.value as any }))}
                              className="input w-full"
                            >
                              <option value="SOLID">Sólido</option>
                              <option value="OUTLINE">Contorno</option>
                              <option value="GHOST">Fantasma</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-surface-300 mb-2">
                              Estilo da Sidebar
                            </label>
                            <select
                              value={formData.sidebarStyle || 'DARK'}
                              onChange={(e) => setFormData(prev => ({ ...prev, sidebarStyle: e.target.value as any }))}
                              className="input w-full"
                            >
                              <option value="DARK">Escuro</option>
                              <option value="LIGHT">Claro</option>
                              <option value="TRANSPARENT">Transparente</option>
                            </select>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-surface-300 mb-2">
                              <Moon className="w-4 h-4 inline mr-2" />
                              Modo Escuro
                            </label>
                            <label className="flex items-center gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.darkModeEnabled ?? true}
                                onChange={(e) => setFormData(prev => ({ ...prev, darkModeEnabled: e.target.checked }))}
                                className="w-5 h-5 rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500"
                              />
                              <span className="text-surface-300">Habilitar modo escuro</span>
                            </label>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-surface-300 mb-2">
                              Modo Padrão
                            </label>
                            <select
                              value={formData.defaultMode || 'DARK'}
                              onChange={(e) => setFormData(prev => ({ ...prev, defaultMode: e.target.value as any }))}
                              className="input w-full"
                            >
                              <option value="LIGHT">Claro</option>
                              <option value="DARK">Escuro</option>
                              <option value="SYSTEM">Sistema</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Save Button */}
              <div className="px-6 py-4 border-t border-surface-700 flex justify-end">
                <button
                  onClick={() => saveMutation.mutate()}
                  disabled={saveMutation.isPending}
                  className="btn-primary flex items-center gap-2"
                >
                  {saveMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Salvar Alterações
                </button>
              </div>
            </div>
          </div>

          {/* Preview Panel */}
          <div className="space-y-6">
            <div className="glass rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <Eye className="w-5 h-5 text-primary-400" />
                <h3 className="font-medium text-surface-200">Preview</h3>
              </div>
              <ThemePreview theme={formData} />
            </div>

            {/* Quick Actions */}
            <div className="glass rounded-xl p-6">
              <h3 className="font-medium text-surface-200 mb-4">Ações Rápidas</h3>
              <div className="space-y-2">
                <button
                  onClick={() => setShowTemplates(true)}
                  className="w-full btn-secondary text-sm flex items-center gap-2 justify-center"
                >
                  <Sparkles className="w-4 h-4" />
                  Aplicar Template
                </button>
                <button
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      primaryColors: defaultPrimaryColors,
                      accentColors: defaultAccentColors,
                      surfaceColors: defaultSurfaceColors,
                    }));
                  }}
                  className="w-full btn-secondary text-sm flex items-center gap-2 justify-center"
                >
                  <RefreshCw className="w-4 h-4" />
                  Resetar Cores
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Templates Modal */}
      <AnimatePresence>
        {showTemplates && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowTemplates(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="glass rounded-xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold text-surface-100 mb-4">Templates de Tema</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {templates?.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => applyTemplateMutation.mutate(template.id)}
                    disabled={applyTemplateMutation.isPending}
                    className="glass rounded-lg p-4 text-left hover:border-primary-500 transition-colors"
                  >
                    <div
                      className="h-20 rounded-lg mb-3"
                      style={{
                        background: `linear-gradient(135deg, rgb(${template.primaryColors['500']}) 0%, rgb(${template.accentColors['500']}) 100%)`,
                      }}
                    />
                    <h4 className="font-medium text-surface-200">{template.name}</h4>
                    {template.description && (
                      <p className="text-xs text-surface-400 mt-1">{template.description}</p>
                    )}
                    {template.isDefault && (
                      <span className="inline-block mt-2 px-2 py-0.5 bg-primary-500/20 text-primary-400 text-xs rounded">
                        Padrão
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ThemeEditor;
